import { Filter } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { apiRequest } from 'common-src/features/rest';

import { pathwayLabel } from '../../constants';
import { formatPatientMrn } from '../../helpers';
import styles from './PatientTaskSearch.module.scss';

const PatientTaskSearch = ({
  eligiblePatientIds = null,
  patientSearch,
  setPatientSearch,
  getPatientSearchLabel,
  getPatientSearchPathway,
  patientSearchMetadataById,
}) => {
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false);
  const [isPatientSearchLoading, setIsPatientSearchLoading] = useState(false);
  const patientSearchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!patientSearchRef.current?.contains(event.target)) {
        setIsPatientSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmedQuery = patientSearchQuery.trim();

    if (patientSearch || trimmedQuery.length < 2) {
      setPatientSearchResults([]);
      setIsPatientSearchOpen(false);
      setIsPatientSearchLoading(false);
      return undefined;
    }

    const shouldRestrictPatientIds = Array.isArray(eligiblePatientIds);
    const eligiblePatientIdSet = new Set(eligiblePatientIds.map((patientId) => `${patientId}`));
    if (shouldRestrictPatientIds && !eligiblePatientIds.length) {
      setPatientSearchResults([]);
      setIsPatientSearchOpen(true);
      setIsPatientSearchLoading(false);
      return undefined;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(() => {
      setIsPatientSearchLoading(true);

      apiRequest({
        endpoint: `members/search/${trimmedQuery}`,
        queryParams: { skip: 0, limit: 20 },
      })
        .then((res) => {
          if (res.status !== 200) throw new Error('Error while retrieving results');
          return res.json();
        })
        .then((data) => {
          if (isCancelled) return;
          const results = Object.values(data)?.[0] || [];
          setPatientSearchResults(
            shouldRestrictPatientIds
              ? results.filter((patient) => eligiblePatientIdSet.has(`${patient?.id ?? ''}`))
              : results,
          );
          setIsPatientSearchOpen(true);
        })
        .catch(() => {
          if (isCancelled) return;
          setPatientSearchResults([]);
          setIsPatientSearchOpen(true);
        })
        .finally(() => {
          if (!isCancelled) setIsPatientSearchLoading(false);
        });
    }, 200);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [eligiblePatientIds, patientSearch, patientSearchQuery]);

  const clearPatientSearch = () => {
    setPatientSearch(null);
    setPatientSearchQuery('');
    setPatientSearchResults([]);
    setIsPatientSearchOpen(false);
  };

  const renderPatientSearchOption = (patient) => {
    const metadata = patientSearchMetadataById[`${patient?.id ?? ''}`] ?? {};
    const firstName = patient?.preferredName || patient?.firstName || '';
    const lastName = patient?.lastName || '';
    const patientName = `${firstName} ${lastName}`.trim() || 'Unknown Patient';
    const pathway = metadata.pathway || getPatientSearchPathway(patient);
    const pathwayDisplay = pathwayLabel[pathway] ?? pathway;
    const phase = patient?.phase ?? metadata.phase ?? 1;
    const clinician =
      patient?.clinician ||
      patient?.providerName ||
      patient?.doctorName ||
      metadata.clinician ||
      null;

    return (
      <div className={styles.patientSearchOption}>
        <div className={styles.patientSearchOptionHeader}>
          <span className={styles.patientSearchOptionName}>{patientName}</span>
          <span className={styles.patientSearchOptionMrn}>
            {metadata.patientMrn || formatPatientMrn(patient?.mrn) || formatPatientMrn(patient?.id)}
          </span>
        </div>
        <div className={styles.patientSearchOptionMeta}>
          {pathwayDisplay ? (
            <span className={styles.patientSearchOptionPathway}>{pathwayDisplay}</span>
          ) : null}
          <span className={styles.patientSearchOptionPhase}>Phase {phase}</span>
          {clinician ? (
            <span className={styles.patientSearchOptionClinician}>{clinician}</span>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div ref={patientSearchRef} className={styles.patientSearchWrap}>
      <div className={styles.patientSearchInput}>
        <Filter className={styles.patientSearchIcon} aria-hidden="true" />
        <input
          id="care-orchestration-task-patient-search"
          type="text"
          className={`${styles.patientSearchNativeInput} ${
            patientSearch ? styles.patientSearchNativeInputSelected : ''
          }`}
          placeholder="Filter patients..."
          value={patientSearch ? getPatientSearchLabel(patientSearch) : patientSearchQuery}
          onChange={(event) => {
            if (patientSearch) return;
            setPatientSearchQuery(event.target.value);
          }}
          onFocus={() => {
            if (!patientSearch && patientSearchResults.length) {
              setIsPatientSearchOpen(true);
            }
          }}
          readOnly={!!patientSearch}
        />
        {(patientSearch || patientSearchQuery) && (
          <button
            type="button"
            onClick={clearPatientSearch}
            className={styles.patientSearchClearIcon}
            aria-label="Clear patient search"
          >
            x
          </button>
        )}
      </div>
      {isPatientSearchOpen && !patientSearch ? (
        <div className={styles.patientSearchDropdown}>
          <div className={styles.patientSearchDropdownHeader}>
            {isPatientSearchLoading
              ? 'Searching...'
              : `${patientSearchResults.length} ${
                  patientSearchResults.length === 1 ? 'patient' : 'patients'
                } found`}
          </div>
          {patientSearchResults.length ? (
            patientSearchResults.map((patient) => (
              <button
                key={`${patient?.id ?? ''}`}
                type="button"
                className={styles.patientSearchResult}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setPatientSearch(patient);
                  setPatientSearchQuery('');
                  setPatientSearchResults([]);
                  setIsPatientSearchOpen(false);
                }}
              >
                {renderPatientSearchOption(patient)}
              </button>
            ))
          ) : (
            <div className={styles.patientSearchEmpty}>
              No patients match &quot;{patientSearchQuery.trim()}&quot;
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

PatientTaskSearch.propTypes = {
  eligiblePatientIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  patientSearch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredName: PropTypes.string,
    mrn: PropTypes.string,
    phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pathway: PropTypes.string,
    clinician: PropTypes.string,
    providerName: PropTypes.string,
    doctorName: PropTypes.string,
    cognitiveStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    diabetesStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setPatientSearch: PropTypes.func,
  getPatientSearchLabel: PropTypes.func,
  getPatientSearchPathway: PropTypes.func,
  patientSearchMetadataById: PropTypes.objectOf(
    PropTypes.shape({
      pathway: PropTypes.string,
      phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      clinician: PropTypes.string,
      patientMrn: PropTypes.string,
    }),
  ),
};

export default PatientTaskSearch;
